import { useCallback, useMemo, useRef, useState } from 'react';
import { worklogApi } from '../../api/worklogApi';
import type { WorklogTimelineItem } from '../../types/worklog.types';
import { buildServerShare, type Designation } from './feedModel';

/**
 * 인디포토 담기(선정) 상태 + 저장 — 공사현황 전체 화면과 나의 현장 '공사현황' 탭이 같이 쓴다.
 *
 * 선정(작업) 대상은 **현장보고 1건 단위**다(기획안). 카드의 [사진선택]을 누르면 **그 카드만** 선정 UI로 바뀌고,
 * 카드 하단 자체 바에서 취소/완료한다. 다른 카드는 평소 모습 그대로 유지된다. 고객공유 선정은 진입점5로 이관됐다(REQ-4-06).
 */
export function useIndiPhotoWork(feedItems: WorklogTimelineItem[]) {
  const [workTarget, setWorkTarget] = useState<{ frId: number; mode: 'photo' } | null>(null);
  /** 저장 진행 중인 FR — 완료 버튼 중복 클릭 방지 */
  const [savingFrId, setSavingFrId] = useState<number | null>(null);
  const [designations, setDesignations] = useState<Record<number, Designation>>({});
  // 이번 세션에 인디포토로 전송 성공한 frPhotoId — 재전송 방지(§3.8은 추가 전송이라 되돌리는 unsend 없음).
  const ipSentRef = useRef<Set<number>>(new Set());
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // 서버가 내려준 기존 선별(feed 기반) — fieldReportId → 인디포토 전송된 사진 index(REQ-4-04)
  const serverShare = useMemo(() => buildServerShare(feedItems), [feedItems]);

  // FR의 초기 선별 상태 — 사용자가 아직 안 건드린 경우의 기준(서버 선별 복원: 인디포토 전송).
  const initialDesignation = useCallback(
    (frId: number): Designation => ({ ipPhotos: new Set(serverShare[frId]?.ipPhotos ?? []) }),
    [serverShare],
  );

  const getDesignation = (frId: number): Designation => designations[frId] ?? initialDesignation(frId);

  const togglePhoto = (frId: number, idx: number) =>
    setDesignations((prev) => {
      const cur = prev[frId] ?? initialDesignation(frId);
      const nextSet = new Set(cur.ipPhotos);
      if (nextSet.has(idx)) nextSet.delete(idx);
      else nextSet.add(idx);
      return { ...prev, [frId]: { ...cur, ipPhotos: nextSet } };
    });

  const enterWork = (frId: number) => setWorkTarget({ frId, mode: 'photo' });

  /** 취소 — 저장하지 않고 이 FR의 편집분을 버려 진입 전 상태로 되돌린다. */
  const cancelWork = () => {
    if (!workTarget) return;
    const { frId } = workTarget;
    setDesignations((prev) => {
      if (!(frId in prev)) return prev;
      const next = { ...prev };
      delete next[frId]; // 로컬 편집분 제거 → getDesignation이 서버 기준(initialDesignation)으로 복귀
      return next;
    });
    setWorkTarget(null);
  };

  /** 완료 — 현재 선정 중인 **그 FR 1건만** 저장한다(인디포토 전송만 남음, REQ-4-06). */
  const saveWork = async () => {
    if (!workTarget) return;
    const { frId } = workTarget;
    const fr = feedItems.find(
      (f) => f.type === 'FIELD_REPORT' && f.fieldReport?.fieldReportId === frId,
    )?.fieldReport;
    if (!fr) {
      setWorkTarget(null);
      return;
    }
    const d = getDesignation(frId);
    setSavingFrId(frId);

    try {
      // 인디포토 전송(§3.8) — '새로 지정된(아직 미전송)' 사진만 전송(POST 추가). 해제는 되돌리지 않는다.
      const alreadySentPis = new Set(serverShare[frId]?.ipPhotos ?? []); // 서버 기준 전송된 사진 index
      const toSend = [...d.ipPhotos]
        .filter((pi) => !alreadySentPis.has(pi)) // 서버에서 이미 전송된 건 제외
        .map((pi) => fr.photos[pi]?.frPhotoId)
        .filter((id): id is number => id != null)
        .filter((id) => !ipSentRef.current.has(id)); // 이번 세션에 이미 보낸 건 제외

      if (toSend.length === 0) {
        setWorkTarget(null);
        setToastMsg('전송할 새 사진이 없습니다.');
        return;
      }

      const res = await worklogApi.sendIndiPhotos(frId, toSend);
      // 일부 사진만 등록에 실패(FAILED)할 수 있다 — 성공분만 '보낸 사진'으로 기록하고, 실패분은 선정 상태를 유지해 다시 보낼 수 있게 한다.
      const failedIds = new Set(res.data.data.sent.filter((s) => s.status === 'FAILED').map((s) => s.frPhotoId));
      const okIds = toSend.filter((id) => !failedIds.has(id));
      okIds.forEach((id) => ipSentRef.current.add(id));
      if (failedIds.size > 0) {
        setToastMsg(`인디포토로 ${okIds.length}장 전송했고, ${failedIds.size}장은 전송하지 못했습니다. 다시 시도해주세요.`);
        return;
      }
      setWorkTarget(null);
      setToastMsg(`인디포토로 ${okIds.length}장 전송했습니다.`);
    } catch {
      setToastMsg('인디포토 전송에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSavingFrId(null);
    }
  };

  const clearToast = useCallback(() => setToastMsg(null), []);

  return {
    workTarget, savingFrId, getDesignation, togglePhoto,
    enterWork, cancelWork, saveWork, toastMsg, clearToast,
  };
}

export type IndiPhotoWork = ReturnType<typeof useIndiPhotoWork>;
